/**
 * One field and one button. Time-to-table for a new player: type a name, one
 * biometric prompt. Returning players get conditional mediation on the same
 * page, so signing in is an autofill tap rather than a second screen.
 */
import { useEffect, useRef, useState } from 'react'
import { createAccount, signIn, supportsAutofill } from './passkey'
import type { Account } from './session'

export function CreateAccount({
  heading,
  blurb,
  cta,
  onDone,
}: {
  heading: string
  blurb?: React.ReactNode
  cta: string
  onDone: (account: Account) => void
}): JSX.Element {
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const cancelled = useRef(false)

  // Conditional mediation runs silently alongside the form: if this device
  // already holds a passkey for cards, the keyboard offers it and the player
  // never types a name at all.
  useEffect(() => {
    cancelled.current = false
    void (async () => {
      if (!(await supportsAutofill())) return
      try {
        const result = await signIn(true)
        if (!cancelled.current) onDone(result.user)
      } catch {
        /* no passkey offered, or the player ignored it — the form is the path */
      }
    })()
    return () => {
      cancelled.current = true
    }
  }, [onDone])

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      const result = await createAccount(name.trim())
      onDone(result.user)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not work')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="live">
      <h2>{heading}</h2>
      {blurb ? <p>{blurb}</p> : null}
      <label className="field">
        <input
          value={name}
          onChange={(e) => setName(e.target.value.slice(0, 24))}
          placeholder="Your name at the table"
          aria-label="Display name"
          autoComplete="username webauthn"
          enterKeyHint="go"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && name.trim().length > 0) void submit()
          }}
        />
      </label>
      {error ? <p className="error">{error}</p> : null}
      <button
        className="btn btn-primary btn-block"
        disabled={name.trim().length === 0 || busy}
        onClick={() => void submit()}
      >
        {busy ? 'Waiting for your device…' : cta}
      </button>
      <button className="btn btn-quiet btn-block" disabled={busy} onClick={() => void signIn().then((r) => onDone(r.user)).catch(() => setError('No passkey for this device'))}>
        I already have a passkey
      </button>
    </section>
  )
}
